import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import wwCalc from "../assets/logo_color.png";
import script from '../script_wwcalc.json';
import { app } from "../config/firebaseConfig";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { sendWhatsAppMessage } from "../services/whatsappAPI";
import InterviewLayout from "./InterviewLayout";
import { useSignedVideos } from "../hooks/useSignedVideos";
import { usePlayback } from "../hooks/usePlayback";
import { useMicControl } from "../hooks/useMicControl";
import wwcalcConfig from "../configs/wwcalc.config";
import { requestFullscreen } from "../utils/fullscreen";


export default function WWCalc() {
  const [isStarted, setIsStarted] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoSpeed, setVideoSpeed] = useState(1);
  const [waitingForResponse, setWaitingForResponse] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [videoUrls, setVideoUrls] = useState({});
  const [userInfo, setUserInfo] = useState({ name: "", contact: "" });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showCaptions, setShowCaptions] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(
    wwcalcConfig.avatars[0]?.id || "default"
  );
  const videoRef = useRef(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const isVideoPlaying = useRef(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isListening,
    isTranscribing,
    transcript,
    resetTranscript,
    toggleListening
  } = useMicControl();

  const questions = script.entrevista.flatMap(section => section.perguntas || []);

  const avatarCfg = wwcalcConfig.avatars.find(a => a.id === selectedAvatar) || wwcalcConfig.avatars[0];
  const signedVideos = useSignedVideos(typeof avatarCfg.path === 'function' ? avatarCfg.path(wwcalcConfig.interviewType) : avatarCfg.path);
  useEffect(() => {
    setVideoUrls(signedVideos);
  }, [signedVideos]);

  useEffect(() => {
    if (transcript) {
      setUserInput(transcript);
    }
  }, [transcript]);

  usePlayback(videoRef, videoSpeed);

  const handleSpeedChange = (speed) => {
    setVideoSpeed(speed);
    if (videoRef.current) {
      videoRef.current.playbackRate = speed;
    }
  };

  const handleStart = () => {
    requestFullscreen();
    setIsStarted(true);
  }; 

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (userInfo.name && userInfo.contact && termsAccepted && privacyAccepted) {
      setFormSubmitted(true);
      setMessages([{ type: 'ia', text: script.entrevista[0].intro }]);
      setIsPlaying(true);
    }
  };

  const handleVideoPlay = () => {
    isVideoPlaying.current = true;
  };

  const handleVideoEnd = async () => {
    isVideoPlaying.current = false;
    if (currentVideoIndex === wwcalcConfig.videoSequence.length - 1) {
      setWaitingForResponse(true);
    } else {
      setIsPlaying(false);
      setWaitingForResponse(true);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (userInput.trim() !== "") {
      setMessages((prev) => [...prev, { type: 'user', text: userInput }]);
      setUserInput("");
      resetTranscript();
      setWaitingForResponse(false);
      
      if (currentVideoIndex === wwcalcConfig.videoSequence.length - 2) {
        setMessages((prev) => [...prev, { type: 'ia', text: script.entrevista.find((item) => item.fim)?.fim }]);
        setCurrentVideoIndex((prev) => prev + 1);
        setIsPlaying(true);
      } else if (currentVideoIndex === wwcalcConfig.videoSequence.length - 1) {
        saveToDatabase(userInput);
      } else {
        const nextQuestion = questions[currentQuestionIndex]?.texto;
        if (nextQuestion) {
          setTimeout(() => {
            setMessages((prev) => [...prev, { type: 'ia', text: nextQuestion }]);
            setIsPlaying(true);
            setCurrentQuestionIndex((prev) => prev + 1);
            if (currentVideoIndex < wwcalcConfig.videoSequence.length - 1) {
              setCurrentVideoIndex((prev) => prev + 1);
            }
          }, 500);
        }
      }
    }
  };

  const saveToDatabase = async (finalResponse) => {
    const formattedMessages = messages
    .map((message, index) => {
      if (message.type === 'ia') {
        return {
          question: message.text,
          answer: messages[index + 1]?.type === 'user' ? messages[index + 1].text : null
        };
      }
      return null;
    })
    .filter(Boolean);

    const lastQuestionIndex = formattedMessages.findIndex(
      (item) => item.question === script.entrevista.find((item) => item.fim)?.fim
    );

    if (lastQuestionIndex !== -1) {
      formattedMessages.splice(lastQuestionIndex, 1); 
    }

    if (finalResponse) {
      formattedMessages.push({
        question: script.entrevista.find((item) => item.fim)?.fim,
        answer: finalResponse
      });
    }

    try {
      setIsLoading(true);
      const functions = getFunctions(app, "southamerica-east1");
      const generateChatGPTReportWWCalc = httpsCallable(functions, "generateChatGPTReportWWCalc");
      // A análise é gravada pelo servidor (eia/wwcalc/estrada) e os e-mails recebem só o id dela:
      // destinatário e conteúdo não saem mais do navegador. Ver emailAnaliseWWCalc.js nas functions.
      const response = await generateChatGPTReportWWCalc({ responses: formattedMessages, userInfo });
      const analysisId = response.data.analysisId;
      const sendAnalysisEmail = httpsCallable(functions, "sendAnalysisEmail");

      const formatPhoneNumber = (phone) => {
        phone = phone.replace(/\D/g, ""); 
        if (!phone.startsWith("55")) {
          return `55${phone}`; 
        }
        return phone;
      };

      const analysis = response.data.report;

      await sendAnalysisEmail({ analysisId });

      const sendPositiveAnalysisEmail = httpsCallable(functions, "sendPositiveAnalysisEmail");
      const sendNegativeAnalysisEmail = httpsCallable(functions, "sendNegativeAnalysisEmail");

      const isWWCalcRecommended = (analysis) => {
        const positiveKeywords = [
          "área rural", "não precisa de precisão", "não há restrições",
          "não deseja pontes", "traçado mais detalhado", "pronto para validação técnica"
        ];
        
        const negativeKeywords = [
          "precisão para duplicações", "posicionamento exato de ruas",
          "área urbanizada", "infraestrutura próxima impactando", "necessita de alta precisão"
        ];

        const positiveMatches = positiveKeywords.filter(keyword => analysis.toLowerCase().includes(keyword)).length;
        const negativeMatches = negativeKeywords.filter(keyword => analysis.toLowerCase().includes(keyword)).length;

        return positiveMatches > negativeMatches;
      };

      const isEmail = userInfo.contact.includes("@");
      const isPhoneNumber = /^\d{10,15}$/.test(userInfo.contact);

      if (isEmail) {
        if (isWWCalcRecommended(analysis)) {
          await sendPositiveAnalysisEmail({ analysisId });
        } else {
          await sendNegativeAnalysisEmail({ analysisId });
        }
      } else if (isPhoneNumber) {
        const formattedPhone = formatPhoneNumber(userInfo.contact);
        try {
          const isPositive = isWWCalcRecommended(analysis);
          await sendWhatsAppMessage({
            phoneNumber: formattedPhone,
            userName: userInfo.name,
            isPositive
          });
        } catch (error) {
          console.error("Erro ao enviar mensagem no WhatsApp:", error);
        }
      } else {
        console.log("Nenhum contato válido foi fornecido.");
      }

      setIsLoading(false); 
      setShowConfirmationModal(true);
    } catch (error) {
      console.error("Erro ao salvar a entrevista: ", error);
    } 
  };

  return (
    <Wrapper>
      {isLoading && (
        <LoadingOverlay>
          <LoadingSpinner />
          <p>Processando sua entrevista, por favor, aguarde...</p>
        </LoadingOverlay>
      )}

      {!isStarted ? (
        <Container>
          <img src={wwCalc} alt="WWCalc Logo" />
          <h1>Seja bem-vindo(a) à EIA da WWCalc.</h1>
          <Button onClick={handleStart}><p>Iniciar</p></Button>
        </Container>
      ) : !formSubmitted ? (
        <FormContainer onSubmit={handleFormSubmit}>
          <Title>Preencha suas informações</Title>
          <Input
            placeholder="Nome"
            value={userInfo.name}
            onChange={(e) => setUserInfo({ ...userInfo, name: e.target.value })}
            required
          />
          <Input
            placeholder="E-mail ou Telefone"
            value={userInfo.contact}
            onChange={(e) => setUserInfo({ ...userInfo, contact: e.target.value })}
            required
          />
          <TermsContainer>
            <Checkbox>
              <input 
                type="checkbox" 
                checked={termsAccepted} 
                onChange={(e) => {
                  setTermsAccepted(e.target.checked);
                  if (e.target.checked) {
                    window.open("https://due-prd.web.app/termos-de-uso.pdf#toolbar=0", "_blank");
                  }
                }} 
                required 
              />
              <label>Li e concordo com os <a href="https://due-prd.web.app/termos-de-uso.pdf#toolbar=0" target="_blank" rel="noopener noreferrer">Termos de Uso</a></label>
            </Checkbox>
            <Checkbox>
              <input 
                type="checkbox" 
                checked={privacyAccepted} 
                onChange={(e) => {
                  setPrivacyAccepted(e.target.checked);
                  if (e.target.checked) {
                    window.open("https://due-prd.web.app/politica-de-privacidade.pdf#toolbar=0", "_blank");
                  }
                }} 
                required 
              />
              <label>Li e concordo com a <a href="https://due-prd.web.app/politica-de-privacidade.pdf#toolbar=0" target="_blank" rel="noopener noreferrer">Política de Privacidade</a></label>
            </Checkbox>
          </TermsContainer>
          <Button type="submit"><p>Enviar</p></Button>
        </FormContainer>
      ) : (
        <InterviewLayout
          controlsProps={{
            features: wwcalcConfig.features,
            tourId: 'step-speed',
            avatars: wwcalcConfig.avatars,
            selectedAvatar,
            onAvatarChange: setSelectedAvatar,
            showCaptions,
            onToggleCaptions: () => setShowCaptions((prev) => !prev),
            videoSpeed,
            onSpeedChange: handleSpeedChange,
          }}
          videoProps={{
            ref: videoRef,
            preload: "auto",
            autoPlay: true,
            onPlay: handleVideoPlay,
            onEnded: handleVideoEnd,
            type: "video/mp4",
            src: videoUrls[wwcalcConfig.videoSequence[currentVideoIndex]] || "",
          }}
          messages={messages}
          showCaptions={showCaptions}
          isPlaying={isPlaying}
          isListening={isListening}
          isTranscribing={isTranscribing}
          userInput={userInput}
          onInputChange={(e) => setUserInput(e.target.value)}
          onSend={handleSendMessage}
          onToggleMic={() => toggleListening(setUserInput)}
          sendDisabled={!waitingForResponse || isListening}
        />
      )}
      {showConfirmationModal && (
        <Modal>
          <ModalContent>
            <p>Entrevista enviada com sucesso! Obrigado por participar.</p>
            <button onClick={() => setShowConfirmationModal(false)}>Fechar</button>
          </ModalContent>
        </Modal>
      )}
    </Wrapper>
  );
}

const Wrapper = styled.div`
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fb;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background-color: #f8f9fb;
  color: #333;
  h1 {
    font-size: 42px;
    color: #333;
    font-family: "Poppins", sans-serif;
    margin-top: 20px;
    text-align: center;
    line-height: 50px;
  }

  @media only screen and (max-width: 600px) {
        img {
          width: 350px;
        }
    }
`

const Button = styled.button`
    margin-top: 40px;
    width: 340px;
    height: 64px;
    background-color: #000;
    box-shadow: 4px 4px 6px #BACCDE89;
    border: none;
    border-radius: 16px;
    cursor: pointer;
    p {
        margin-top: 0;
        color: #fff;
        font-family: "Poppins", sans-serif;
        font-size: 18px;
    }
`;

const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 30px;
  width: 100%;
  max-width: 400px;
`;

const Title = styled.h2`
  font-family: "Poppins", sans-serif;
  font-size: 22px;
  font-weight: 600;
  color: #333333;
  margin-bottom: 20px;
`;

const Input = styled.input`
  font-family: "Poppins", sans-serif;
  width: 100%;
  padding: 12px;
  margin: 10px 0;
  font-size: 14px;
  border: 1px solid #d1d1d1;
  border-radius: 8px;
  outline: none;
  transition: all 0.2s ease-in-out;

  &:focus {
    border-color: #000;
    box-shadow: 0 0 5px rgba(0, 0, 0, 0.2);
  }
`;

const TermsContainer = styled.div`
  margin: 20px 0;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Checkbox = styled.label`
  display: flex;
  align-items: center;
  font-family: "Poppins", sans-serif;
  font-size: 14px;
  color: #555555;

  input {
    margin-right: 10px;
    width: 16px;
    height: 16px;
  }

  a {
    color: #007bff;
    font-weight: 600;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  text-align: center;
  font-family: "Poppins", sans-serif;
  font-size: 18px;

  button {
    margin-top: 20px;
    padding: 10px 20px;
    border: none;
    background: #000;
    color: #fff;
    font-weight: 600;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background: #333;
    }
  }
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  z-index: 2000; 
  p {
    font-family: "Poppins", sans-serif;
    font-size: 18px;
  }
`;

const LoadingSpinner = styled.div`
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;
